import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { sanitizeRichHtml } from '@/lib/richtext'
import { FileText, Loader2, X, Download, AlertCircle } from 'lucide-react'

/** Pretty filename from a storage URL (strip query, decode escapes). */
function filenameFromUrl(url: string): string {
  try {
    const u = new URL(url)
    const last = u.pathname.split('/').filter(Boolean).pop() ?? u.host
    return decodeURIComponent(last)
  } catch {
    return url
  }
}

/** Lightweight in-browser .docx preview. Mammoth is dynamically imported the
 *  first time the modal opens, so it costs nothing in the main bundle and the
 *  document never leaves the browser (no third-party viewer — keeps client
 *  files within the app, in line with the data-residency expectation).
 *
 *  We render mammoth's HTML output through the same DOMPurify sanitizer the
 *  rich text uses, then drop it into a scrollable sheet. Deliberately minimal:
 *  no pagination, no page chrome — just a readable text view of the document. */
export function DocxPreview({
  url,
  onClose,
}: {
  /** Public URL of the .docx file. Null = closed. */
  url: string | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [html, setHtml] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const reqId = useRef(0)

  useEffect(() => {
    if (!url) return
    const id = ++reqId.current
    setHtml(null)
    setError(false)
    setLoading(true)
    ;(async () => {
      try {
        // Use the prebuilt BROWSER bundle — the default `mammoth` entry pulls
        // Node-only deps and throws on load in the browser.
        const [mod, res] = await Promise.all([
          import('mammoth/mammoth.browser'),
          fetch(url),
        ])
        const convertToHtml = mod.convertToHtml ?? mod.default?.convertToHtml
        if (!convertToHtml) throw new Error('mammoth.convertToHtml unavailable')
        if (!res.ok) throw new Error(`fetch ${res.status}`)
        const buf = await res.arrayBuffer()
        const out = await convertToHtml({ arrayBuffer: buf })
        if (reqId.current !== id) return // a newer open superseded this one
        const safe = sanitizeRichHtml(out.value || '')
        setHtml(safe || `<p>${t('docx.empty')}</p>`)
      } catch (err) {
        console.error('docx preview failed', err)
        if (reqId.current === id) setError(true)
      } finally {
        if (reqId.current === id) setLoading(false)
      }
    })()
  }, [url, t])

  // Escape closes.
  useEffect(() => {
    if (!url) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [url, onClose])

  if (!url) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]">
        {/* Header */}
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border-soft)] px-4 py-3">
          <FileText className="h-4 w-4 shrink-0 text-[var(--color-link)]" />
          <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[var(--color-text-strong)]">
            {filenameFromUrl(url)}
          </span>
          <a
            href={url}
            download
            title={t('docx.download')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <Download className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-[var(--color-surface-2)]/30 px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-[var(--color-text-dim)]">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-[12.5px]">{t('docx.loading')}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-[var(--color-text-dim)]">
              <AlertCircle className="h-6 w-6 opacity-50" />
              <p className="text-[12.5px]">{t('docx.error')}</p>
              <a
                href={url}
                download
                className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
              >
                <Download className="h-3.5 w-3.5" />
                {t('docx.download')}
              </a>
            </div>
          ) : (
            <div
              className="docx-preview mx-auto max-w-[680px] rounded-xl bg-[var(--color-surface)] px-8 py-7 text-[13.5px] leading-relaxed text-[var(--color-text)] shadow-[var(--shadow-soft)]"
              // Mammoth output, already DOMPurify-sanitized above.
              dangerouslySetInnerHTML={{ __html: html ?? '' }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
